import { ColumnDef } from '@tanstack/react-table';

import { Approval } from '@/app/org-cfv/src/typings/ActivityDataDetailsTypings';
import { SegmentActivity } from '@/app/org-cfv/src/typings/ActivityDataSchema';
import { EmissionsType } from '@/app/org-cfv/src/typings/CreateActivityDataTypings';
import { BUSINESS_TRIP_TYPE_TO_I18N_KEY } from '@/constants/common';
import { NextTFunction } from '@/i18n/client';
import { toThousandthString } from '@/utils/numbers';

// This file contains the columns for cat3.3 / cat3.4

export const getEmployeeIdHeader = (
  t: NextTFunction,
  isBulkPreview: boolean = false,
): ColumnDef<Approval & EmissionsType> => {
  const accessorKey = isBulkPreview ? 'metadata.employeeId' : 'activityData.metadata.employeeId';
  return {
    header: t('org-cfv-create-emission.employeeID'),
    accessorKey,
  };
};

export const getEmployeeIdentifierHeader = (
  t: NextTFunction,
  isBulkPreview: boolean = false,
): ColumnDef<Approval & EmissionsType> => {
  const accessorKey = isBulkPreview ? 'metadata.employeeIdentifier' : 'activityData.metadata.employeeIdentifier';
  return {
    header: t('org-cfv-create-emission.employeeID'),
    accessorKey,
  };
};

export const getEmployeeNameHeader = (
  t: NextTFunction,
  isBulkPreview: boolean = false,
): ColumnDef<Approval & EmissionsType> => {
  const accessorKey = isBulkPreview ? 'metadata.employeeName' : 'activityData.metadata.employeeName';
  return {
    header: t('common.name'),
    accessorKey,
  };
};

export const getEmployeeDepartmentHeader = (
  t: NextTFunction,
  isBulkPreview: boolean = false,
): ColumnDef<Approval & EmissionsType> => {
  const accessorKey = isBulkPreview ? 'metadata.department' : 'activityData.metadata.department';
  return {
    header: t('org-cfv-create-emission.DivisionOrDepartment'),
    accessorKey,
  };
};

export const getEmployeeDivisionDepartmentHeader = (
  t: NextTFunction,
  isBulkPreview: boolean = false,
): ColumnDef<Approval & EmissionsType> => {
  const accessorKey = isBulkPreview
    ? 'metadata.employeeDivisionDepartment'
    : 'activityData.metadata.employeeDivisionDepartment';
  return {
    header: t('org-cfv-create-emission.DivisionOrDepartment'),
    accessorKey,
  };
};

export const getCommutingRouteNameHeader = (
  t: NextTFunction,
  isBulkPreview: boolean = false,
): ColumnDef<Approval & EmissionsType> => {
  const accessorKey = isBulkPreview ? 'metadata.commutingRouteName' : 'activityData.metadata.commutingRouteName';
  return {
    header: t('common.commutingRouteName'),
    accessorKey,
  };
};

export const getTransMeansHeader = (
  t: NextTFunction,
  isBulkPreview: boolean = false,
): ColumnDef<Approval & EmissionsType> => {
  const accessorKey = isBulkPreview ? 'metadata.segmentActivity' : 'activityData.metadata.segmentActivity';
  return {
    header: t('fd-rmt.transMeans'),
    accessorKey,
    cell: ({ getValue }) => {
      return (getValue() as SegmentActivity[])
        ?.map((segment) => t(('fd-rmt.' + segment.transMeans) as string))
        .join(',');
    },
  };
};

export const getCommutingDaysHeader = (
  t: NextTFunction,
  isBulkPreview: boolean = false,
): ColumnDef<Approval & EmissionsType> => {
  const accessorKey = isBulkPreview ? 'metadata.commutingDays' : 'activityData.metadata.commutingDays';
  return {
    header: t('org-cfv-create-emission.NumberOfCommutingDays'),
    accessorKey,
  };
};

export const getCommutingDistanceHeader = (
  t: NextTFunction,
  isBulkPreview: boolean = false,
): ColumnDef<Approval & EmissionsType> => {
  const accessorKey = isBulkPreview ? 'metadata.usageAmount' : 'activityData.metadata.usageAmount';
  return {
    header: t('org-cfv-create-emission.CommutingTotalDistanceKM'),
    accessorKey,
  };
};

export const getCommutingTotalDistanceHeader = (
  t: NextTFunction,
  isBulkPreview: boolean = false,
): ColumnDef<Approval & EmissionsType> => {
  const accessorKey = isBulkPreview ? 'metadata.distance' : 'activityData.metadata.distance';
  return {
    header: t('org-cfv-create-emission.CommutingTotalDistanceKM'),
    accessorKey,
    cell: ({ getValue }) => toThousandthString(getValue() as number),
  };
};

export const getDepartureCountryArea = (
  t: NextTFunction,
  translate: (country: string) => string,
  isBulkPreview: boolean = false,
): ColumnDef<Approval & EmissionsType> => {
  const accessorKey = isBulkPreview ? 'metadata.departure.region' : 'activityData.metadata.departure.region';
  return {
    header: t('org-cfv-create-emission.DepartureCountry'),
    accessorKey,
    cell: ({ getValue }) => translate(getValue() as string),
  };
};

export const getDeparturePostalCode = (
  t: NextTFunction,
  isBulkPreview: boolean = false,
): ColumnDef<Approval & EmissionsType> => {
  const accessorKey = isBulkPreview ? 'metadata.departure.postalCode' : 'activityData.metadata.departure.postalCode';
  return {
    header: t('org-cfv-create-emission.DepartureZipCode'),
    accessorKey,
  };
};

export const getDepartureAddress = (
  t: NextTFunction,
  isBulkPreview: boolean = false,
): ColumnDef<Approval & EmissionsType> => {
  const accessorKey = isBulkPreview ? 'metadata.departure.address' : 'activityData.metadata.departure.address';
  return {
    header: t('org-cfv-create-emission.DepartureAddress'),
    accessorKey,
  };
};

export const getBusinessTransModeHeader = (
  t: NextTFunction,
  isBulkPreview: boolean = false,
): ColumnDef<Approval & EmissionsType> => {
  const accessorKey = isBulkPreview ? 'metadata.transMode' : 'activityData.metadata.transMode';
  return {
    header: t('fd-rmt.transMeans'),
    accessorKey,
    cell: ({ getValue }) => {
      return t('fd-rmt.' + (getValue() as string));
    },
  };
};

export const getDestinationCountryArea = (
  t: NextTFunction,
  translate: (key: string) => string,
  isBulkPreview: boolean = false,
): ColumnDef<Approval & EmissionsType> => {
  const accessorKey = isBulkPreview ? 'metadata.destination.region' : 'activityData.metadata.destination.region';
  return {
    header: t('org-cfv-create-emission.DestinationCountry'),
    accessorKey,
    cell: ({ getValue }) => translate(getValue() as string),
  };
};

export const getDestinationPostalCode = (
  t: NextTFunction,
  isBulkPreview: boolean = false,
): ColumnDef<Approval & EmissionsType> => {
  const accessorKey = isBulkPreview
    ? 'metadata.destination.postalCode'
    : 'activityData.metadata.destination.postalCode';
  return {
    header: t('org-cfv-create-emission.DestinationZipCode'),
    accessorKey,
  };
};

export const getDestinationAddress = (
  t: NextTFunction,
  isBulkPreview: boolean = false,
): ColumnDef<Approval & EmissionsType> => {
  const accessorKey = isBulkPreview ? 'metadata.destination.address' : 'activityData.metadata.destination.address';
  return {
    header: t('org-cfv-create-emission.DestinationAddress'),
    accessorKey,
  };
};

export const getDistanceHeader = (
  t: NextTFunction,
  isBulkPreview: boolean = false,
): ColumnDef<Approval & EmissionsType> => {
  const accessorKey = isBulkPreview ? 'metadata.usageAmount' : 'activityData.metadata.usageAmount';
  return {
    header: t('common.distance'),
    accessorKey,
  };
};

export const getBusinessTravelUnitHeader = (
  t: NextTFunction,
  {
    label,
    isBulkPreview = false,
  }: {
    label?: string;
    isBulkPreview?: boolean;
  } = {},
): ColumnDef<Approval & EmissionsType> => {
  const accessorKey = isBulkPreview ? 'metadata.unit' : 'activityData.metadata.unit';
  return {
    header: label || t('org-cfv-create-emission.unit'),
    accessorKey,
    cell: ({ getValue }) => t('unit.' + (getValue() as string)),
  };
};

export const getBusinessTripTypeHeader = (
  t: NextTFunction,
  isBulkPreview: boolean = false,
): ColumnDef<Approval & EmissionsType> => {
  const accessorKey = isBulkPreview ? 'metadata.businessTripType' : 'activityData.metadata.businessTripType';
  return {
    header: t('org-cfv-data-entry.typesOfBusinessTravel'),
    accessorKey,
    cell: ({ getValue }) => t(BUSINESS_TRIP_TYPE_TO_I18N_KEY[getValue() as string]),
  };
};
export const getAccommodationHeader = (
  t: NextTFunction,
  isBulkPreview: boolean = false,
): ColumnDef<Approval & EmissionsType> => {
  const accessorKey = isBulkPreview ? 'metadata.usageAmount' : 'activityData.metadata.usageAmount';
  return {
    header: t('common.accommodationActivitiesQuantity'),
    accessorKey,
  };
};
export const getRoomTypeHeader = (
  t: NextTFunction,
  isBulkPreview: boolean = false,
): ColumnDef<Approval & EmissionsType> => {
  const accessorKey = isBulkPreview ? 'metadata.accommodationMode' : 'activityData.metadata.accommodationMode';
  return {
    header: t('common.roomType'),
    accessorKey,
  };
};
